import CustomTooltip from '../../../shared/CustomTooltip';
import {
    Box,
    Grid,
    List,
    ListItem,
    Paper,
    Typography,
    IconButton,
    Stack,
    Button,
    CircularProgress,
} from '@mui/material';
import React, { useState, MouseEvent } from 'react';
import moment from 'moment-timezone';
import ProfilePicture from '../../Profile/ProfilePicture';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import CustomNoRowsOverlay from '../../../shared/NoRowsOverlayCustom';
import UserRole from '../../../../shared/AuthRoles';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';
import { theme } from '../../../../shared/theme';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { styled } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { BookingAttendee } from '../../../types/reservation';
import { HourglassEmpty } from '@mui/icons-material';
import { capitalizeName } from '../../../../shared/utils';
import { useSendRsvpRemindersMutation } from '../../../../app/Slices/SevenDayApiSlice';
import toast from '../../../shared/Toast';

const headerTypographySx = {
    fontWeight: 700,
    letterSpacing: 0.2,
};

const CleanDataGrid = (props: any) => (
    <DataGrid
        {...props}
        sx={{
            fontFamily: ['Inter'].join(','),
            border: 0,
            boxShadow: 0,
            '& .MuiDataGrid-columnHeader': {
                borderBottom: `1px solid ${theme.palette.custom.lightBorderColor}`,
                backgroundColor: theme.palette.neutral.main,
            },
            '& .MuiDataGrid-columnHeaders': {
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                overflow: 'hidden',
                position: 'sticky',
                top: 0,
                zIndex: 2,
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
                paddingLeft: 0,
                paddingRight: 0,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
                width: '100%',
                fontWeight: 800,
                fontSize: 12,
            },
            '& .MuiDataGrid-row': {
                borderBottom: `1px solid ${theme.palette.custom.lightBorderColor}`,
            },
            '& .MuiDataGrid-row:hover': {
                backgroundColor: theme.palette.custom.cardActionBackground,
            },
            '& .MuiDataGrid-cell': {
                whiteSpace: 'normal',
                lineHeight: '1.4rem',
                padding: '10px 8px',
                overflow: 'visible',
                display: 'flex',
                alignItems: 'center',
            },
            '& .MuiDataGrid-virtualScroller': {
                overflowX: 'hidden',
            },
            '& .MuiDataGrid-main': { overflowX: 'hidden' },
            '& .MuiDataGrid-footerContainer': {
                borderTop: `1px solid ${theme.palette.custom.lightBorderColor}`,
                justifyContent: 'flex-end',
                minHeight: 52,
            },
            '& .MuiTablePagination-root': {
                width: 'auto',
            },
            '& .MuiTablePagination-toolbar': {
                width: 'auto',
                paddingLeft: 0,
                paddingRight: 0,
                minHeight: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0,
            },
            '& .MuiTablePagination-spacer': {
                display: 'none',
            },
            '& .MuiTablePagination-selectLabel': {
                margin: 0,
                marginRight: 0,
                whiteSpace: 'nowrap',
            },
            '& .MuiTablePagination-input': {
                margin: 0,
                marginRight: 10,
            },
            '& .MuiTablePagination-displayedRows': {
                margin: 0,
                marginLeft: 0,
                marginRight: 10,
                whiteSpace: 'nowrap',
            },
            '& .MuiTablePagination-actions': {
                marginLeft: 0,
            },
            ...(props?.sx || {}),
        }}
    />
);

const WhiteTooltip = styled(({ className, ...props }: any) => (
    <CustomTooltip {...props} classes={{ popper: className }} arrow />
))(({ theme }) => ({
    [`& .MuiTooltip-tooltip`]: {
        backgroundColor: theme.palette.neutral.main,
        color: theme.palette.primary.main,
        fontSize: 12,
        fontWeight: 600,
        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        padding: '8px 12px',
    },
    [`& .MuiTooltip-arrow`]: {
        color: theme.palette.neutral.main,
    },
}));

const RsvpTable = ({
    bookingId,
    accepted,
    declined,
    pending,
    reservationStatus,
    isMobile,
}: {
    bookingId: number;
    accepted: BookingAttendee[];
    declined: BookingAttendee[];
    pending: BookingAttendee[];
    reservationStatus: string;
    isMobile: boolean;
}) => {
    const loggedInUser = UserRole();

    const [sendReminders] = useSendRsvpRemindersMutation();

    const handleSendReminder = async ({
        e,
        bookingId,
        rsvpId,
        userId,
    }: {
        e: MouseEvent<HTMLButtonElement>;
        bookingId: number;
        rsvpId: string;
        userId: number;
    }) => {
        const key = String(userId);

        setSubmittingRows((prev) => ({ ...prev, [key]: true }));
        try {
            e.stopPropagation();
            await sendReminders({
                bookingId,
                rsvpIds: [rsvpId],
            }).unwrap();

            toast.success(`Reminder sent successfully.`);
        } catch {
            toast.error('Error responding to RSVP');
        }
        setSubmittingRows((prev) => ({ ...prev, [key]: false }));
    };

    const [submittingRows, setSubmittingRows] = useState<
        Record<string, boolean>
    >({});

    const slugify = (v: any) =>
        String(v ?? '')
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');

    const getUserProfileHref = (row: any) => {
        const firstName = slugify(row?.firstName || 'user');
        const id = row?.id ?? '';
        return `/users/${firstName}/${id}`;
    };

    const openUserProfile = (row: any) => {
        const href = getUserProfileHref(row);
        window.open(href, '_blank', 'noopener,noreferrer');
    };

    const renderDetailsList = (row: BookingAttendee) => {
        const {
            rsvp: { status, requestSentAt, lastReminderSentAt, respondedAt },
        } = row;
        return (
            <List
                disablePadding
                sx={{
                    listStyleType: 'disc',
                    pl: 2,
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    fontSize: '12px',
                    '& .MuiListItem-root': {
                        fontSize: '12px',
                    },
                }}
            >
                {status === 'pending' ? (
                    <>
                        <ListItem sx={{ padding: 0, display: 'list-item' }}>
                            RSVP request sent at:{' '}
                            {requestSentAt
                                ? moment(new Date(requestSentAt)).format(
                                      'D MMM, h:mm A',
                                  )
                                : 'N/A'}
                        </ListItem>
                        <ListItem sx={{ padding: 0, display: 'list-item' }}>
                            Last reminder sent at:{' '}
                            {lastReminderSentAt
                                ? moment(new Date(lastReminderSentAt)).format(
                                      'D MMM, h:mm A',
                                  )
                                : 'N/A'}
                        </ListItem>
                    </>
                ) : (
                    <ListItem sx={{ padding: 0, display: 'list-item' }}>
                        {`${capitalizeName(status)} at ${
                            respondedAt
                                ? moment(new Date(respondedAt)).format(
                                      'D MMM, h:mm A',
                                  )
                                : 'N/A'
                        }`}
                    </ListItem>
                )}
            </List>
        );
    };

    const MobileUserCard = ({ row }: { row: any }) => {
        const userId = Number(row?.id);
        const key = String(userId);

        const isRowSubmitting = submittingRows[key] || false;

        const roleBlocked =
            loggedInUser.role.includes('owner') ||
            (loggedInUser.role.includes('user') &&
                loggedInUser?.sub !== row?.id);

        const disabled =
            isRowSubmitting ||
            !row?.isActive ||
            roleBlocked ||
            reservationStatus === 'pending';

        return (
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                    backgroundColor: theme.palette.neutral.main,
                    boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ p: 1.25 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1.25,
                            alignItems: 'flex-start',
                        }}
                    >
                        <ProfilePicture
                            profileDetails={row}
                            src={row?.profilePicture?.src}
                            sx={{ width: 42, height: 42, flexShrink: 0 }}
                        />

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        minWidth: 0,
                                        flex: 1,
                                        cursor: 'pointer',
                                        '&:active .name': {
                                            textDecoration: 'underline',
                                        },
                                    }}
                                    onClick={() => openUserProfile(row)}
                                    role='link'
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (
                                            e.key === 'Enter' ||
                                            e.key === ' '
                                        ) {
                                            e.preventDefault();
                                            openUserProfile(row);
                                        }
                                    }}
                                >
                                    <Typography
                                        className='name'
                                        sx={{
                                            fontWeight: 950,
                                            fontSize: 14,
                                            lineHeight: 1.15,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {row?.firstName
                                            ? `${row.firstName} ${row?.lastName ?? ''}`.trim()
                                            : 'External Guest'}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: theme.palette.text.secondary,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            mt: 0.25,
                                        }}
                                    >
                                        {row?.email}
                                    </Typography>
                                </Box>

                                <Stack
                                    direction='row'
                                    spacing={0.25}
                                    alignItems='center'
                                >
                                    <WhiteTooltip title='View profile'>
                                        <IconButton
                                            onClick={() => openUserProfile(row)}
                                            sx={{
                                                p: 0.5,
                                                borderRadius: 2,
                                                backgroundColor: 'transparent',
                                                '&:hover': {
                                                    backgroundColor:
                                                        theme.palette.custom
                                                            .cardActionBackground,
                                                },
                                            }}
                                        >
                                            <OpenInNewIcon
                                                sx={{ fontSize: 20 }}
                                            />
                                        </IconButton>
                                    </WhiteTooltip>
                                </Stack>
                            </Box>

                            <Box sx={{ mt: 1.1 }}>{renderDetailsList(row)}</Box>

                            {row?.rsvp.status === 'pending' && (
                                <Box
                                    sx={{
                                        mt: 1.1,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Button
                                        fullWidth
                                        size='small'
                                        variant='contained'
                                        color='primary'
                                        disabled={disabled}
                                        onClick={(
                                            e: MouseEvent<HTMLButtonElement>,
                                        ) =>
                                            handleSendReminder({
                                                e,
                                                bookingId,
                                                rsvpId: row?.rsvp.id,
                                                userId: row?.id,
                                            })
                                        }
                                        sx={{
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            boxShadow: 'none',
                                            height: 32,
                                            fontSize: 12,
                                            fontWeight: 700,
                                            ...(disabled
                                                ? { opacity: 0.7 }
                                                : {}),
                                        }}
                                    >
                                        {isRowSubmitting ? (
                                            <CircularProgress
                                                size={18}
                                                sx={{ color: 'inherit' }}
                                            />
                                        ) : (
                                            'Send reminder'
                                        )}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Paper>
        );
    };

    if (isMobile) {
        return (
            <Stack spacing={1.25}>
                {accepted.length ? (
                    <Box>
                        <Stack spacing={1.25}>
                            {accepted.map((row: any) => (
                                <MobileUserCard key={row?.id} row={row} />
                            ))}
                        </Stack>
                    </Box>
                ) : null}

                {declined.length ? (
                    <Box sx={{ mt: accepted.length ? 1.25 : 0 }}>
                        <Stack spacing={1.25}>
                            {declined.map((row: any) => (
                                <MobileUserCard key={row?.id} row={row} />
                            ))}
                        </Stack>
                    </Box>
                ) : null}

                {pending.length ? (
                    <Box sx={{ mt: accepted.length ? 1.25 : 0 }}>
                        <Stack spacing={1.25}>
                            {pending.map((row: any) => (
                                <MobileUserCard key={row?.id} row={row} />
                            ))}
                        </Stack>
                    </Box>
                ) : null}
            </Stack>
        );
    }

    const HeaderCell = ({
        label,
        align,
    }: {
        label: string;
        align?: 'left' | 'center' | 'right';
    }) => (
        <Typography
            component='h6'
            variant='body3'
            sx={{
                ...headerTypographySx,
                width: '100%',
                textAlign: align || 'left',
            }}
        >
            {label}
        </Typography>
    );

    const GuestCell = (params: any) => {
        const href = getUserProfileHref(params.row);

        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                    minWidth: 0,
                }}
            >
                <Box
                    role='link'
                    tabIndex={0}
                    onClick={() => openUserProfile(params.row)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openUserProfile(params.row);
                        }
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        minWidth: 0,
                        flex: 1,
                        cursor: 'pointer',
                        '&:hover .name': { textDecoration: 'underline' },
                    }}
                >
                    <ProfilePicture
                        profileDetails={params.row}
                        src={params.row?.profilePicture?.src}
                        sx={{ width: 40, height: 40, flexShrink: 0 }}
                    />

                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            className='name'
                            sx={{
                                fontWeight: 800,
                                fontSize: 13,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {params.row?.firstName
                                ? `${params.row.firstName} ${params.row?.lastName ?? ''}`.trim()
                                : 'External Guest'}
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: 12,
                                color: theme.palette.text.secondary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {params.row?.email}
                        </Typography>
                    </Box>
                </Box>

                <WhiteTooltip title='View profile'>
                    <IconButton
                        size='small'
                        onClick={() =>
                            window.open(href, '_blank', 'noopener,noreferrer')
                        }
                        sx={{ flexShrink: 0 }}
                    >
                        <OpenInNewIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </WhiteTooltip>
            </Box>
        );
    };

    const AccessStatusCell = (params: any) => {
        const status = params.row.rsvp.status;

        const color =
            status === 'accepted'
                ? 'green'
                : status === 'declined'
                  ? 'red'
                  : 'gray';

        const icon =
            status === 'accepted' ? (
                <CheckCircleIcon sx={{ fontSize: 16 }} />
            ) : status === 'declined' ? (
                <CancelIcon sx={{ fontSize: 16 }} />
            ) : (
                <HourglassEmpty sx={{ fontSize: 16 }} />
            );

        return (
            <CustomChip
                label={
                    <Stack direction='row' spacing={0.75} alignItems='center'>
                        {icon}
                        <Typography
                            variant='body3'
                            sx={{ fontSize: 11, fontWeight: 800 }}
                        >
                            {capitalizeName(status)}
                        </Typography>
                    </Stack>
                }
                color={color}
                variant='outlined'
                sx={{
                    height: 32,
                    px: 1.25,
                    '& .MuiChip-label': { px: 0 },
                }}
            />
        );
    };

    const ActionButtonCell = (params: any) => {
        if (params.row.rsvp.status !== 'pending') return '';
        const isRowSubmitting = submittingRows[params.id] || false;
        const disabled = isRowSubmitting || reservationStatus === 'pending';

        return (
            <Box
                sx={{
                    width: '100%',
                }}
            >
                <Button
                    variant='contained'
                    color='primary'
                    disabled={disabled}
                    onClick={(e: MouseEvent<HTMLButtonElement>) =>
                        handleSendReminder({
                            e,
                            bookingId,
                            rsvpId: params.row.rsvp.id,
                            userId: params.row.id,
                        })
                    }
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        height: 34,
                        minWidth: 150,
                    }}
                >
                    {isRowSubmitting ? (
                        <CircularProgress size={18} sx={{ color: 'inherit' }} />
                    ) : (
                        'Send reminder'
                    )}
                </Button>
            </Box>
        );
    };

    const DetailsWrap = (children: React.ReactNode) => (
        <Box
            sx={{
                width: '100%',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                overflow: 'visible',
            }}
        >
            {children}
        </Box>
    );

    const DetailsToggleCell = (params: any) => {
        return (
            <Box sx={{ width: '100%' }}>
                <Box sx={{ mt: 1 }}>
                    {DetailsWrap(renderDetailsList(params.row))}
                </Box>
            </Box>
        );
    };

    const columnsProvidedTo: GridColDef[] = [
        {
            field: 'firstName',
            headerName: 'Guest',
            flex: 1.4,
            minWidth: 220,
            renderHeader: () => <HeaderCell label='Guest' />,
            renderCell: GuestCell,
        },
        {
            field: 'status',
            headerName: 'Response',
            flex: 0.8,
            minWidth: 140,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            disableColumnMenu: true,
            renderHeader: () => <HeaderCell label='Response' align='center' />,
            renderCell: (params: any) => AccessStatusCell(params),
        },
        {
            field: 'details',
            headerName: '',
            flex: 1.2,
            minWidth: 200,
            sortable: false,
            disableColumnMenu: true,
            renderHeader: () => <HeaderCell label='' />,
            valueGetter: () => '',
            cellClassName: 'detailsCell',
            renderCell: DetailsToggleCell,
        },
        ...(pending.length
            ? [
                  {
                      field: 'id',
                      headerName: 'Actions',
                      flex: 0.7,
                      minWidth: 150,
                      align: 'center',
                      headerAlign: 'center',
                      sortable: false,
                      disableColumnMenu: true,
                      renderHeader: () => (
                          <HeaderCell label='Actions' align='center' />
                      ),
                      renderCell: (params: any) => ActionButtonCell(params),
                  } as GridColDef,
              ]
            : []),
    ];

    const gridCommonSx = {
        width: '100%',
        '& .MuiDataGrid-cell': {
            whiteSpace: 'normal',
            lineHeight: '1.4rem',
            padding: '10px 8px',
            overflow: 'visible',
            alignItems: 'flex-start',
            py: 1,
        },
        '& .detailsCell': {
            overflow: 'visible',
        },
    };

    return (
        <>
            {accepted.length > 0 && (
                <Paper
                    elevation={0}
                    square
                    sx={{
                        backgroundColor: theme.palette.neutral.main,
                        p: { xs: 2, sm: 3, md: 3.5 },
                        boxShadow: 'none',
                        mb: 2,
                    }}
                >
                    <Grid item xs={12}>
                        <CleanDataGrid
                            getRowHeight={() => 'auto'}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: 10 },
                                },
                            }}
                            pageSizeOptions={[10, 25, 50]}
                            disableColumnFilter
                            disableColumnSelector
                            disableDensitySelector
                            rows={accepted}
                            columns={columnsProvidedTo}
                            slots={{ noRowsOverlay: CustomNoRowsOverlay }}
                            sx={gridCommonSx}
                        />
                    </Grid>
                </Paper>
            )}

            {declined.length > 0 && (
                <Paper
                    elevation={0}
                    square
                    sx={{
                        backgroundColor: theme.palette.neutral.main,
                        p: { xs: 2, sm: 3, md: 3.5 },
                        boxShadow: 'none',
                        mb: 2,
                    }}
                >
                    <Grid item xs={12}>
                        <CleanDataGrid
                            getRowHeight={() => 'auto'}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: 10 },
                                },
                            }}
                            pageSizeOptions={[10, 25, 50]}
                            disableColumnFilter
                            disableColumnSelector
                            disableDensitySelector
                            rows={declined}
                            columns={columnsProvidedTo}
                            slots={{ noRowsOverlay: CustomNoRowsOverlay }}
                            sx={gridCommonSx}
                        />
                    </Grid>
                </Paper>
            )}

            {pending.length > 0 && (
                <Paper
                    elevation={0}
                    square
                    sx={{
                        backgroundColor: theme.palette.neutral.main,
                        p: { xs: 2, sm: 3, md: 3.5 },
                        boxShadow: 'none',
                        mb: 2,
                    }}
                >
                    <Grid item xs={12}>
                        <CleanDataGrid
                            getRowHeight={() => 'auto'}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: 10 },
                                },
                            }}
                            pageSizeOptions={[10, 25, 50]}
                            disableColumnFilter
                            disableColumnSelector
                            disableDensitySelector
                            rows={pending}
                            columns={columnsProvidedTo}
                            slots={{ noRowsOverlay: CustomNoRowsOverlay }}
                            sx={gridCommonSx}
                        />
                    </Grid>
                </Paper>
            )}
        </>
    );
};

export default RsvpTable;
